// The one form of the Teams screen (SCR-573): "New team" asks for a name and the id the proxy
// will see in the `team_id` claim; "Rename" asks for the name alone (the API refuses changing the
// id). Validation runs on submit; a refused write lands on its field (409 `team_exists` on the id)
// or under the form, and the dialog stays open with the values typed, so nothing changes locally
// until the server has said yes. Read-only mode disables submit (TASK-579).
import { useState } from "preact/hooks";
import { useReadOnly } from "../../host/readonly";
import { Dialog } from "../../primitives/dialog";
import { errorId, Field } from "../../primitives/field";
import { hasErrors, type FieldErrors } from "../../primitives/forms";
import { asApiError, validateTeamForm, writeFailure, type Team, type TeamFormMode, type TeamFormValues } from "./model";

export interface TeamDialogProps {
  mode: TeamFormMode;
  initial: TeamFormValues;
  /** The write; resolves to the team as the server now holds it. */
  submit(values: TeamFormValues): Promise<Team>;
  onSaved(team: Team): void;
  onClose(): void;
  readOnly?: boolean;
  reason?: string | null;
}

const TITLES: Record<TeamFormMode, { title: string; action: string }> = {
  create: { title: "New team", action: "Create" },
  rename: { title: "Rename team", action: "Save" },
};

export function TeamDialog({ mode, initial, submit, onSaved, onClose, readOnly: propsReadOnly, reason: propsReason }: TeamDialogProps) {
  const contextReadOnly = useReadOnly();
  const readOnly = propsReadOnly ?? contextReadOnly.readOnly;
  const reason = propsReason ?? contextReadOnly.reason;
  const [values, setValues] = useState<TeamFormValues>(initial);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const words = TITLES[mode];

  const set = (field: keyof TeamFormValues) => (event: Event) => {
    setValues((current) => ({ ...current, [field]: (event.currentTarget as HTMLInputElement).value }));
  };

  const save = async () => {
    const problems = validateTeamForm(mode, values);
    setErrors(problems);
    setFailure(null);
    if (hasErrors(problems)) return;
    setBusy(true);
    try {
      onSaved(await submit(values));
    } catch (error) {
      const refusal = writeFailure(asApiError(error));
      if (refusal.field) setErrors({ [refusal.field]: refusal.message });
      else setFailure(refusal.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={words.title}
      open
      onClose={onClose}
      actions={
        <>
          <button type="button" class="button button-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" class="button button-primary" onClick={save} disabled={readOnly || busy} title={readOnly ? reason ?? "" : undefined} aria-describedby={readOnly ? "teams-stale-note" : undefined}>
            {busy ? "Saving…" : words.action}
          </button>
        </>
      }
    >
      <form
        class="team-form"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Field id="team-name" label="Name" help="teams.name" error={errors.name}>
          <input
            id="team-name"
            type="text"
            value={values.name}
            maxLength={80}
            autocomplete="off"
            aria-describedby={errors.name ? errorId("team-name") : undefined}
            onInput={set("name")}
          />
        </Field>
        {mode === "create" && (
          <Field id="team-external-id" label="Team id" help="teams.externalId" error={errors.externalId} hint="What the proxy sees in the team_id claim or a team mapping; it cannot be changed later.">
            <input
              id="team-external-id"
              type="text"
              value={values.externalId}
              maxLength={200}
              autocomplete="off"
              spellcheck={false}
              aria-describedby={errors.externalId ? errorId("team-external-id") : undefined}
              onInput={set("externalId")}
            />
          </Field>
        )}
        {failure && (
          <p class="banner banner-error" role="alert">
            {failure}
          </p>
        )}
      </form>
    </Dialog>
  );
}
